import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { getEnv } from '@unni/platform'

import {
  markPaywallDismissed,
  markUserLoggedIn,
  type SigninPaywallProps as ConditionProps,
  isCrawler,
  isUserLoggedIn,
  isKoreanUser,
  isIncludedPath,
  isWithinPaywallTimeRange,
  isUtmUser,
  isTestUser,
  hasUtmParameters,
  markUtmUserPermanent,
  SigninPaywallProps,
} from '../../utils/signin-paywall-conditions'
import PaywallOverlay from '../paywall-overlay'

import { postAmplitude } from '@/core/lib'
import { markPaywallShown } from '@/utils/login-source'
import { useLocales } from '@/shared/hooks/useLocales'

/**
 * 경로에 따른 pageType 결정
 */
const getPageType = (pathname: string): 'community' | 'post' | 'column' | 'tip' => {
  if (pathname.includes('/community/')) return 'community'
  if (pathname.includes('/posts/')) return 'post'
  if (pathname.includes('/columns/')) return 'column'
  if (pathname.includes('/tips/')) return 'tip'
  return 'community' // 기본값
}

/**
 * SigninPaywall을 표시할지 결정하는 메인 함수
 */
export const shouldShowSigninPaywall = (props: SigninPaywallProps): boolean => {
  // local 환경은 표시하지 않음
  if (getEnv('NEXT_PUBLIC_ENV') === 'local') return false
  // 크롤러는 절대 표시하지 않음
  if (isCrawler()) return false
  // 로그인한 사용자는 절대 표시하지 않음
  if (isUserLoggedIn(props)) return false
  // UTM 유저는 절대 표시하지 않음
  if (isUtmUser()) return false
  // 테스트 유저는 절대 표시하지 않음
  if (isTestUser()) return false
  // 한국 사용자가 아니면 표시하지 않음
  if (!isKoreanUser(props)) return false
  // 제외 경로에서는 표시하지 않음
  if (!isIncludedPath(props)) return false

  // 모든 조건을 충족하면 표시
  return true
}

/**
 * 전역 SigninPaywall 컴포넌트
 * _app.tsx에서 사용되며, 조건에 따라 자동으로 표시/숨김
 */
const SigninPaywall = ({ isLoggedIn }: { isLoggedIn: boolean }): JSX.Element | null => {
  const { locale } = useLocales()
  const router = useRouter()
  const [isVisible, setIsVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  // 조건 체크 및 표시 여부 결정
  useEffect(() => {
    // UTM 파라미터 감지 및 영구 저장
    if (hasUtmParameters()) {
      markUtmUserPermanent()
    }

    const condition: ConditionProps = {
      isLoggedIn,
      currentPath: router.pathname,
      language: locale,
    }

    const shouldShow = shouldShowSigninPaywall(condition)
    setIsVisible(shouldShow)
    setIsLoading(false)

    // Analytics 이벤트
    if (shouldShow) {
      // Paywall 표시됨을 세션에 기록
      markPaywallShown()

      // 기존 이벤트 유지
      postAmplitude('signin_paywall_shown', {
        page_path: router.pathname,
        language: locale,
      })

      // 새로운 통합 이벤트 추가
      postAmplitude('paywall_shown', {
        page_path: router.pathname,
        language: locale,
      })
    }
  }, [isLoggedIn, router.pathname, locale])

  // 로그인 상태 변경 시 처리
  useEffect(() => {
    if (isLoggedIn) {
      markUserLoggedIn()
      setIsVisible(false)
    }
  }, [isLoggedIn])

  // Paywall 닫기 처리
  const handleClose = () => {
    markPaywallDismissed()
    setIsVisible(false)

    // 기존 이벤트 유지
    postAmplitude('signin_paywall_dismissed', {
      page_path: router.pathname,
      language: locale,
    })

    // 새로운 통합 이벤트 추가
    postAmplitude('paywall_dismissed', {
      page_path: router.pathname,
      language: locale,
    })
  }

  // 로딩 중이거나 표시하지 않을 경우 null 반환
  if (isLoading || !isVisible) {
    return null
  }

  return (
    <PaywallOverlay
      isVisible={isVisible}
      onClose={handleClose}
      pageType={getPageType(router.pathname)}
      contentId={router.pathname}
      contentCategory="web"
    />
  )
}

export default SigninPaywall
